import {useEffect, useRef} from 'react'
import {documentToPlainTextString} from '@contentful/rich-text-plain-text-renderer'
import {documentToHtmlString} from '@contentful/rich-text-html-renderer'
import {
  getGenericContentLink,
  resolveGenericContentCta,
  type GenericContent,
} from '../../../../../../brand/lib/types/contentful'
import {animateFadeIn, type AnimationElement} from '../../_utils/animations'
import {animateParallax, type AnimationParallaxElement} from '../../_utils/parallax'

import {Heading, Prose} from '@primer/react-brand/esm'
import {MediaAsset} from '../../_shared/MediaAsset/MediaAsset'
import {CtaButtons} from '../../_shared/CtaButtons/CtaButtons'
import {Eyebrow} from '../../_shared/Eyebrow/Eyebrow'
import {Link} from '../../_shared/Link/Link'
import {Spacer} from '../../_shared/Spacer/Spacer'

import Styles from './Highlight.module.css'

type Props = {
  content: GenericContent
  location: string
  sectionIndex: number
}

export function Highlight(props: Props) {
  const {content, location, sectionIndex} = props
  const {heading, subheading, label, text, media} = content.fields

  const title = heading
  const subTitle = subheading ? documentToPlainTextString(subheading) : ''

  const eyebrow = label?.fields.text
  const description = text ? documentToHtmlString(text) : ''

  const link = getGenericContentLink(content)
  const ctaGroup = resolveGenericContentCta(content, {legacyFallback: 'first'})

  // Animation
  const wrapper1Ref = useRef<HTMLDivElement>(null)
  const wrapper2Ref = useRef<HTMLDivElement>(null)
  const wrapper3Ref = useRef<HTMLDivElement>(null)

  const headingRef = useRef<HTMLDivElement>(null)
  const assetRef = useRef<HTMLDivElement>(null)
  const descriptionRef = useRef<HTMLDivElement>(null)
  const linkRef = useRef<HTMLDivElement>(null)
  const ctaRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const elements1: AnimationElement[] = []
    if (headingRef.current) elements1.push({element: headingRef.current})
    const cleanup1 = wrapper1Ref.current ? animateFadeIn(elements1, wrapper1Ref.current) : undefined

    const elements2: AnimationParallaxElement[] = []
    if (assetRef.current) elements2.push({element: assetRef.current})
    const cleanup2 = wrapper2Ref.current ? animateParallax(elements2, wrapper2Ref.current) : undefined

    const elements3: AnimationElement[] = []
    if (descriptionRef.current) elements3.push({element: descriptionRef.current})
    if (linkRef.current) elements3.push({element: linkRef.current})
    if (ctaRef.current) elements3.push({element: ctaRef.current})
    const cleanup3 = wrapper3Ref.current ? animateFadeIn(elements3, wrapper3Ref.current) : undefined

    return () => {
      cleanup1?.()
      cleanup2?.()
      cleanup3?.()
    }
  }, [])

  return (
    <div className={Styles.Highlight}>
      {title && (
        <div ref={wrapper1Ref}>
          <Spacer size="64px" size1280="128px" />
          <Heading className={Styles.Title} data-heading="1" as={sectionIndex === 0 ? 'h2' : 'h3'} ref={headingRef}>
            {title}
          </Heading>
          <Spacer size="32px" size1280="64px" />
        </div>
      )}

      <div className={Styles.River}>
        <div className={Styles.Cell} ref={wrapper2Ref}>
          <div className={Styles.EyebrowWrapper}>
            {subTitle && <Eyebrow className={Styles.SubTitle} text={subTitle} hideIcon />}
            {eyebrow && <Eyebrow className={Styles.EyebrowMobile} text={eyebrow} aria-hidden="true" />}
          </div>

          <Spacer size="24px" size1280="32px" />

          {media && (
            <div className={Styles.MediaWrapper}>
              <div ref={assetRef} data-animate="container">
                <div data-animate="item">
                  <MediaAsset content={media} location={location} />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={Styles.Cell} ref={wrapper3Ref}>
          <div className={Styles.Content}>
            <div>
              {eyebrow && <Eyebrow className={Styles.EyebrowDesktop} text={eyebrow} />}

              {description && (
                <>
                  <Spacer size="24px" size1280="32px" />
                  <div ref={descriptionRef}>
                    <Prose className={Styles.Description} html={description} />
                  </div>
                </>
              )}

              {link && (
                <>
                  <Spacer size="24px" size1280="32px" />
                  <div ref={linkRef}>
                    <Link href={link.fields.href} text={link.fields.text} location={location} />
                  </div>
                </>
              )}
            </div>

            {ctaGroup && (
              <>
                <Spacer size="64px" />
                <div ref={ctaRef}>
                  <CtaButtons contentfulContent={ctaGroup} location={location} />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
